import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import ChannelList from './ChannelList';
import EventList from './EventList';
import { LogOut, ArrowLeft } from 'lucide-react';
import useDarkMode from '../hooks/useDarkMode';
import ThemeToggle from './ThemeToggle';
import { setToken, removeToken, getToken } from '../services/api';

export default function ChannelEventFlow() {
  const [user, setUser] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const { darkMode, toggle: toggleDark } = useDarkMode();

  const LOCAL_KEY = 'pupl_user';

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      const token = getToken();
      if (!raw || !token) {
        localStorage.removeItem(LOCAL_KEY);
        removeToken();
        return;
      }
      const obj = JSON.parse(raw);
      // JWT has 24h expiry; also check local expiry
      if (obj && obj.expiresAt && obj.expiresAt > Date.now()) {
        setUser({ id: obj.id, username: obj.username, email: obj.email });
      } else {
        localStorage.removeItem(LOCAL_KEY);
        removeToken();
      }
    } catch (err) {
      console.error('Local storage parse error:', err);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser({ id: userData.id, username: userData.username, email: userData.email });
    // Store JWT token
    if (userData.token) {
      setToken(userData.token);
    }
    const store = {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours to match JWT
    };
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(store)); } catch (e) { console.warn('Unable to persist session', e); }
  };

  const handleLogout = () => {
    localStorage.removeItem(LOCAL_KEY);
    removeToken();
    setUser(null);
    setSelectedChannel(null);
  };

  const handleBackToChannels = () => {
    setSelectedChannel(null);
  };

  // Not logged in
  if (!user) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '1.5rem 1rem' }}>
      <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
        {/* Top Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          padding: '0.75rem 1rem',
          background: 'var(--bg-elevated)',
          borderRadius: 'var(--radius-md)',
          border: '1.5px solid var(--border-color)',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {selectedChannel && (
              <button
                onClick={handleBackToChannels}
                style={{
                  background: 'transparent',
                  border: '1.5px solid var(--border-color)',
                  padding: '0.35rem 0.6rem',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-secondary)',
                  transition: 'all var(--transition-fast)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--primary-400)';
                  e.currentTarget.style.color = 'var(--primary-500)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
                title="Kanallara Dön"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <img
              src="/favicon.svg"
              alt="Pupl Logo"
              style={{
                width: '34px',
                height: '34px',
                borderRadius: 'var(--radius-sm)',
              }}
            />
            <div>
              <span style={{
                color: 'var(--text-primary)',
                fontSize: '1rem',
                fontWeight: '600',
              }}>
                {user.username}
              </span>
              {selectedChannel && (
                <span style={{
                  color: 'var(--text-muted)',
                  fontSize: '0.875rem',
                  marginLeft: '8px',
                }}>
                  / {selectedChannel.name}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ThemeToggle darkMode={darkMode} onToggle={toggleDark} />
            <button
              onClick={handleLogout}
              style={{
                background: 'transparent',
                border: '1.5px solid var(--border-color)',
                padding: '0.45rem 0.85rem',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-secondary)',
                fontWeight: '500',
                fontSize: '0.875rem',
                transition: 'all var(--transition-fast)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--danger-400)';
                e.currentTarget.style.color = 'var(--danger-500)';
                e.currentTarget.style.background = 'var(--danger-50)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.color = 'var(--text-secondary)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <LogOut size={15} />
              Çıkış
            </button>
          </div>
        </div>

        {/* Content */}
        {selectedChannel ? (
          <EventList
            channelId={selectedChannel.id}
            channelName={selectedChannel.name}
          />
        ) : (
          <ChannelList
            userId={user.id}
            onSelectChannel={setSelectedChannel}
          />
        )}
      </div>
    </div>
  );
}
